'use client';

/**
 * @fileoverview `DiscoverResultsBarConnected` — client wrapper that renders the
 * persistent results bar for the discover surface.
 *
 * Hoisted out of `DiscoverListView` (WEB-270) so the bar stays mounted
 * regardless of which view body the user is on. Since WEB-1074 it is the v3.1
 * context bar: count · day-strip slot · Filters (≤980) · Sort (>980) · one Map
 * toggle (the List/Map tablist is retired; List is the resting state).
 *
 * Wires:
 *   - `view`/`sort` from `useDiscoverParams` (URL-state hook).
 *   - `total` + `tasteMatchedTotal` from {@link useDiscoverEnvelopeTotals}
 *     (same `/events/discover` cache entry as the list / chrome — WEB-786).
 *     Pending totals skeleton; a resolved `0` is a measured zero.
 *   - `tasteMatchedTotal` is the SERVER's corpus-wide field (API-1671),
 *     gated on the FRE-917 personalization model. `null` means "not
 *     measurable" and must not become `0`.
 *   - `onViewChange`/`onSortChange` write back through `setParams` so the URL
 *     stays the source of truth. The Map toggle's `aria-pressed` is
 *     `view === 'map'`, so the takeover's close (which writes `view=list`)
 *     un-presses it with no extra wiring.
 *
 * @since WEB-270
 */
import * as React from 'react';

import type { CityCode } from '@loci/city-editions';
import { useDiscoverEventsQuery } from '@loci/api';

import {
  DAY_STRIP_FAR_NOTE_UNORDERED,
  DiscoverDayStripLive,
  useLoadedDayDates,
} from '@/components/molecules/DiscoverDayStrip';
import { DiscoverResultsBar } from '@/components/organisms/DiscoverListView';
import { MobileFilterDrawer } from '@/components/organisms/DiscoverFilterRail/MobileFilterDrawer';
import { DISCOVER_RESULTS_REGION_TEST_ID } from '@/components/organisms/DiscoverFilterRail/mobileFilterSheet.constants';
import { useDiscoverParams } from '@/hooks/useDiscoverParams';
import { useDiscoverClock } from '@/lib/discover/DiscoverClockContext';
import { countActiveFilters } from '@/lib/discover/activeFilterCount';
import { buildDayStripChips, resolveDayStripWindowForResponse } from '@/lib/discover/dayStrip';
import {
  dateForDiscoverResultsBar,
  resolveDiscoverScopeKey,
} from '@/lib/discover/resultsCountCopy';
import { serializeDiscoverParams, type SortMode, type ViewMode } from '@/lib/discover/url-state';
import { useDiscoverEventArgs } from '@/lib/discover/useDiscoverEventArgs';

import { useDiscoverEnvelopeTotals } from './useDiscoverEnvelopeTotals';
import { useDiscoverPersonalization } from './useDiscoverPersonalization';

export interface DiscoverResultsBarConnectedProps {
  /** Active city — drives the geo anchor for the parallel query. */
  cityCode: CityCode;
  /**
   * SSR-rendered total to use until the client query resolves. Optional —
   * when absent the bar skeletons rather than painting `0` (WEB-786).
   */
  initialTotal?: number;
  /** Serialized SSR discover-args scope for the sheet access-card fallback. */
  initialTotalsScope?: string;
  /** Optional class merged onto the bar's outer container. */
  className?: string;
}

/**
 * Connected results bar. Always renders — present in both views (list, map).
 *
 * Uses the shared {@link buildDiscoverQueryArgs} so the bar and `DiscoverListView`
 * subscribe to the SAME RTK Query cache entry (API-1415). Without alignment
 * each subscriber fired its own `/events/discover` request on cold mount and
 * burned the 60/15min anon-IP rate limit.
 */
export function DiscoverResultsBarConnected({
  cityCode,
  initialTotal,
  initialTotalsScope,
  className,
}: DiscoverResultsBarConnectedProps): React.ReactElement {
  const { params, setParams } = useDiscoverParams();
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const filtersTriggerRef = React.useRef<HTMLButtonElement>(null);
  const activeFilterCount = React.useMemo(() => countActiveFilters(params), [params]);

  // FRE-917 — the taste half of the count is gated on an authenticated viewer
  // with a real taste signal. Reads the SAME shared subscription (via
  // `useDiscoverPersonalization`), so no extra fetch fires. The sort LABELS are
  // no longer gated on this (WEB-791 / DPL-16) — only the matched count is.
  const personalization = useDiscoverPersonalization(cityCode);
  const hasTasteSignal = personalization !== null;

  // WEB-786 — live envelope wins; SSR `initialTotal` covers the anonymous
  // seed until the shared subscription lands. Missing both is pending
  // (skeleton), never a painted `0` that later becomes non-zero.
  const totals = useDiscoverEnvelopeTotals(cityCode, { inRangeTotal: initialTotal });
  const total = totals.resolved ? (totals.inRangeTotal ?? null) : null;

  // WEB-667 / DPL-17 — the taste half is the SERVER's corpus-wide
  // `meta.tasteMatchedTotal` (API-1671), gated on the same FRE-917
  // personalization model as the provenance / sort chrome. `null` means
  // "not measurable" and must not become `0`. A real `0` is a measured
  // zero and is passed through.
  const tasteMatchedTotal =
    hasTasteSignal && typeof totals.tasteMatchedTotal === 'number'
      ? totals.tasteMatchedTotal
      : null;

  const dayStrip = useDayStrip(cityCode);

  const handleViewChange = React.useCallback(
    (next: ViewMode) => {
      setParams({ view: next });
    },
    [setParams],
  );

  const handleSortChange = React.useCallback(
    (next: SortMode) => {
      setParams({ sort: next });
    },
    [setParams],
  );

  return (
    <>
      <DiscoverResultsBar
        total={total}
        view={params.view}
        sort={params.sort}
        onViewChange={handleViewChange}
        onSortChange={handleSortChange}
        className={className}
        stripSlot={dayStrip}
        tasteMatchedTotal={tasteMatchedTotal}
        date={dateForDiscoverResultsBar(resolveDiscoverScopeKey(totals.scopeKey, params.date))}
        filtersOpen={filtersOpen}
        onFiltersOpen={() => setFiltersOpen(true)}
        activeFilterCount={activeFilterCount}
        filtersTriggerRef={filtersTriggerRef}
      />
      <MobileFilterDrawer
        cityCode={cityCode}
        initialTotal={initialTotal}
        initialTotalsScope={initialTotalsScope}
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        triggerRef={filtersTriggerRef}
      />
    </>
  );
}

const RESULTS_REGION_SELECTOR = `[data-testid="${DISCOVER_RESULTS_REGION_TEST_ID}"]`;

/**
 * WEB-1079 — the day strip for the bar's slot, or `null` before the Discover
 * clock has mounted (the span depends on city-local "now", so rendering it
 * during SSR would risk a hydration mismatch at midnight).
 *
 * - Span: `resolveDayStripWindowForResponse`. A valid `meta.requestedWindow`
 *   wins; when that field is absent the local resolver uses the same contract.
 * - Loaded days: the `section[data-local-date]` nodes in the results region
 *   (read from the DOM; list pagination stays inside the list).
 * - Live: `DiscoverDayStripLive` runs the scroll-spy, jump and centering
 *   (WEB-1080); under the Price sort the far note says "load more"
 *   rather than promising the tail (DQ-4).
 * - Empty days: `facets.calendar` from the SAME `useDiscoverEventsQuery` cache
 *   entry the list and bar already hold (no extra request). `currentData`, so
 *   a filter change never borrows the previous filter set's counts; a
 *   degraded date facet is treated as unknown.
 */
function useDayStrip(cityCode: CityCode): React.ReactElement | null {
  const { params } = useDiscoverParams();
  const { now, timeZone } = useDiscoverClock();
  const queryArgs = useDiscoverEventArgs(params, cityCode);
  const { currentData } = useDiscoverEventsQuery(queryArgs);
  const loadedDates = useLoadedDayDates(RESULTS_REGION_SELECTOR);

  const facets = currentData?.facets;
  const calendarCounts = facets && !facets.degraded?.includes('date') ? facets.calendar : undefined;

  const span = React.useMemo(
    () =>
      now ? resolveDayStripWindowForResponse(params.date, now, timeZone, currentData?.meta) : null,
    [params.date, now, timeZone, currentData?.meta],
  );
  const chips = React.useMemo(
    () =>
      span
        ? buildDayStripChips({
            range: span.range,
            cityTimezone: timeZone,
            loadedDates,
            calendarCounts,
            scope: span.scope,
          })
        : [],
    [span, timeZone, loadedDates, calendarCounts],
  );
  const moreHref = React.useMemo(() => {
    const query = serializeDiscoverParams({ ...params, date: 'next30' }).toString();
    return query ? `?${query}` : '?date=next30';
  }, [params]);

  if (!span) return null;
  return (
    <DiscoverDayStripLive
      chips={chips}
      scope={span.scope}
      showMoreLink={span.showMoreLink}
      moreHref={moreHref}
      farNote={params.sort === 'price' ? DAY_STRIP_FAR_NOTE_UNORDERED : undefined}
    />
  );
}
